import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Pagination,
  Stack,
  Button,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { getPriorityColor, getUserInfo } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";
import { Delete, SquarePen } from "lucide-react";
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';

// Client IDs that should see the updated UI layout (Mobile, Name, Machine ID, Date & Time, Status)
const UPDATED_UI_CLIENT_IDS = [
  "69afdc065323c68fd76cdabf", // Client ID for updated card layout
];

export const StageColumn = ({
  stage,
  deals,
  onDragOver,
  onDrop,
  onDragStart,
  onEditStage,
  onDeleteStage,
  itemsPerPage = 50,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { userId, userRole } = getUserInfo();
  // Viewers can look at the pipeline but can't drag leads between stages
  const canMoveDeals = userRole !== "viewer";

  // Reset page when stage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [stage._id]);

  // Filter deals by stage
  const stageDealsList = deals.filter((deal) => deal.stage === stage._id);

  // Pagination logic
  const totalPages = Math.ceil(stageDealsList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDeals = stageDealsList.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <Box
      onDragOver={onDragOver}
      onDrop={(e) => canMoveDeals && onDrop(e, stage._id)}
      sx={{
        minWidth: 220,
        maxWidth: 220,
        bgcolor: "#F9FAFB",
        borderRadius: 2,
        p: 2,
        border: "1px solid #E5E7EB",
        display: "flex",
        flexDirection: "column",
        height: "80vh", // 🔒 FIXED HEIGHT
        flexShrink: 0,
      }}
    >
      {/* ================= STAGE HEADER ================= */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} fontSize="0.95rem">
            {stage.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {stageDealsList.length} deals
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            onClick={() => onEditStage?.(stage)}
            sx={{
              minWidth: "auto",
              px: 1,
              py: 0.5,
              color: "#6B7280",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            <SquarePen size={18} />
          </Button>

          <Button
            size="small"
            onClick={() => onDeleteStage?.(stage)}
            sx={{
              minWidth: "auto",
              px: 1,
              py: 0.5,
              color: "#EF4444",
              "&:hover": { bgcolor: "rgba(239,68,68,0.1)" },
            }}
          >
            <Delete size={18} />
          </Button>
        </Stack>
      </Box>

      {/* ================= DEALS LIST (SCROLLABLE) ================= */}
      <Box
        sx={{
          flex: 1,
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D1D5DB",
            borderRadius: 4,
          },
        }}
      >
        {stageDealsList.length === 0 ? (
          <Box
            sx={{
              minHeight: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#9CA3AF",
            }}
          >
            <Typography variant="caption">No deals in this stage</Typography>
          </Box>
        ) : (
          paginatedDeals.map((deal) => {
            const isUpdatedUi = UPDATED_UI_CLIENT_IDS.includes(userId);
            const priorityColors = getPriorityColor(deal.priority);

            if (isUpdatedUi) {
              return (
                <Card
                  key={deal._id || deal.id}
                  draggable={canMoveDeals}
                  onDragStart={(e) => onDragStart?.(e, deal._id || deal.id)}
                  onClick={() => navigate(`${deal._id || deal.id}`)}
                  sx={{
                    cursor: "pointer",
                    borderLeft: `4px solid ${priorityColors.main}`,
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 2 },
                    minHeight: 110,
                    flexShrink: 0,
                  }}
                >
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="0.85rem"
                      color="primary.main"
                      sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
                    >
                      <PhoneIphoneIcon sx={{ mr: 0.5, fontSize: "1rem" }} />{deal.mobile || "—"}
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.primary"
                      sx={{ mb: 0.5, fontSize: "0.8rem" }}
                    >
                      {deal.name || deal.dealName || "—"}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5, fontSize: "0.75rem" }}
                    >
                      <strong>Machine ID:</strong> {deal.machineNumber || "—"}
                    </Typography>

                    {deal.errorCode && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        display="block"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        <strong>Error Code:</strong> {deal.errorCode}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1, fontSize: "0.75rem" }}
                    >
                      <strong>Date & Time:</strong> {deal.createdAt
                        ? new Date(deal.createdAt).toLocaleString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        : "—"}
                    </Typography>

                    <Box sx={{ mt: 0.5 }}>
                      {(() => {
                        const status = deal.complaintStatus;
                        if (!status) {
                          return null;
                        }
                        const s = status.toLowerCase();
                        let bgcolor = "#f1f5f9";
                        let color = "#475569";
                        if (s.includes("resolved") || s.includes("received")) {
                          bgcolor = "#dcfce7";
                          color = "#15803d";
                        } else if (s.includes("credit") || s.includes("refund")) {
                          bgcolor = "#dbeafe";
                          color = "#1e40af";
                        }
                        return (
                          <Chip
                            label={status}
                            size="small"
                            sx={{
                              bgcolor,
                              color,
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              height: 18,
                              borderRadius: 1,
                            }}
                          />
                        );
                      })()}
                    </Box>

                    {(deal.email || deal.customerEmailAddress) && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={deal.email || deal.customerEmailAddress}
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#475569",
                            fontSize: "0.65rem",
                            height: 18,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    )}

                  </CardContent>
                </Card>
              );
            }

            // Previous layout for all other clients
            return (
              <Card
                key={deal._id || deal.id}
                draggable={canMoveDeals}
                onDragStart={(e) => onDragStart?.(e, deal._id || deal.id)}
                onClick={() => navigate(`${deal._id || deal.id}`)}
                sx={{
                  cursor: "pointer",
                  borderLeft: `4px solid ${priorityColors.main}`,
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: 2 },
                  minHeight: 110,
                  flexShrink: 0,
                }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="0.85rem"
                      sx={{
                        display: 'flex',
                        alignItems: "center"
                      }}
                    >
                      <PhoneIphoneIcon sx={{ mr: 0.5, fontSize: "1rem", color: "gray" }} /> {deal.mobile}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    // color="text.secondary"
                    display="block"
                    mb={deal.machineNumber ? 0.5 : 1}
                    sx={{
                      display: 'flex',
                      alignItems: "center"
                    }}
                  >
                    <PermIdentityIcon sx={{ mr: 0.5, fontSize: "1rem", color: "gray" }} /> {deal.name || deal.dealName}
                  </Typography>

                  {deal.machineNumber && (
                    <Chip
                      label={`Machine #${deal.machineNumber}`}
                      size="small"
                      sx={{
                        bgcolor: "#eff6ff",
                        color: "#1e40af",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        height: 18,
                        mb: 1,
                        borderRadius: 1,
                      }}
                    />
                  )}

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1, mb: 1 }}>
                    <Typography
                      variant="caption"
                      // color="text.secondary"
                      sx={{ fontSize: "0.7rem", display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                      {deal.createdAt
                        ? new Date(deal.createdAt).toLocaleString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        : "—"}
                    </Typography>
                    {deal.closingDate && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                      >
                        Closing: {new Date(deal.closingDate).toLocaleDateString("en-AE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Typography>
                    )}
                  </Box>

                  {(deal.email || deal.customerEmailAddress) && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={deal.email || deal.customerEmailAddress}
                        size="small"
                        sx={{
                          bgcolor: "#f1f5f9",
                          color: "#475569",
                          fontSize: "0.65rem",
                          height: 18,
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  )}

                </CardContent>
              </Card>
            );
          })
        )}
      </Box>

      {/* ================= PAGINATION (FIXED) ================= */}
      {totalPages > 1 && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#F9FAFB",
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => setCurrentPage(value)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};
